import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiCall, ApiError } from '../../lib/api.js';
import { useAccessToken, requireToken } from '../../lib/use-access-token.js';

export interface ConnectionDetail {
  provider: string;
  name: string;
  status: 'unconfigured' | 'connected' | 'failing';
  config?: Record<string, unknown>;
  lastTestedAt: string | null;
  lastTestError: string | null;
  lastRotatedAt: string | null;
  configuredAt: string | null;
  configuredBy?: string | null;
  isDefault: boolean;
}

export interface ProbeResultOk {
  ok: true;
  message?: string;
  details?: Record<string, unknown>;
}
export interface ProbeResultFailure {
  ok: false;
  status: number | null;
  reason: string;
}
export type ProbeResult = ProbeResultOk | ProbeResultFailure;

function baseUrl(companyId: string, provider: string, name: string): string {
  return `/v1/companies/${companyId}/integrations/${provider}/connections/${name}`;
}

/**
 * Returns the connection detail, or `null` when the row doesn't exist yet
 * (server returns 404). Either state is valid — the detail page renders
 * an empty form so HR can save for the first time.
 */
export function useConnection(
  companyId: string | undefined,
  provider: string | undefined,
  name: string | undefined,
) {
  const { token } = useAccessToken();
  return useQuery<ConnectionDetail | null, Error>({
    queryKey: ['connection', companyId, provider, name, token],
    enabled: !!companyId && !!provider && !!name && !!token,
    queryFn: async ({ signal }) => {
      if (!companyId || !provider || !name) {
        throw new Error('useConnection requires companyId, provider, and name');
      }
      try {
        return await apiCall<ConnectionDetail>(baseUrl(companyId, provider, name), {
          token: requireToken(token),
          signal,
        });
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    retry: false,
    staleTime: 15_000,
  });
}

export interface SaveInput {
  config: Record<string, unknown>;
  secret?: Record<string, unknown>;
}

export function useSaveConnection(companyId: string, provider: string, name: string) {
  const qc = useQueryClient();
  const { token } = useAccessToken();
  return useMutation<{ arn: string; versionId: string | null }, Error, SaveInput>({
    mutationFn: async (input) =>
      apiCall(baseUrl(companyId, provider, name), {
        method: 'PUT',
        token: requireToken(token),
        body: input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['connection', companyId, provider, name] });
      void qc.invalidateQueries({ queryKey: ['integrations', companyId] });
    },
  });
}

export function useTestConnection(companyId: string, provider: string, name: string) {
  const qc = useQueryClient();
  const { token } = useAccessToken();
  return useMutation<ProbeResult, Error>({
    mutationFn: async () =>
      apiCall(`${baseUrl(companyId, provider, name)}/test`, {
        method: 'POST',
        token: requireToken(token),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['connection', companyId, provider, name] });
      void qc.invalidateQueries({ queryKey: ['integrations', companyId] });
    },
  });
}

export function useRotateSecret(companyId: string, provider: string, name: string) {
  const qc = useQueryClient();
  const { token } = useAccessToken();
  return useMutation<
    { arn: string; versionId: string },
    Error,
    { secret: Record<string, unknown> }
  >({
    mutationFn: async (input) =>
      apiCall(`${baseUrl(companyId, provider, name)}/rotate`, {
        method: 'POST',
        token: requireToken(token),
        body: input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['connection', companyId, provider, name] });
    },
  });
}

export function useDeleteConnection(companyId: string, provider: string, name: string) {
  const qc = useQueryClient();
  const { token } = useAccessToken();
  return useMutation<void, Error>({
    mutationFn: async () => {
      await apiCall(baseUrl(companyId, provider, name), {
        method: 'DELETE',
        token: requireToken(token),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['connection', companyId, provider, name] });
      void qc.invalidateQueries({ queryKey: ['integrations', companyId] });
    },
  });
}
